import React, { useState, useEffect, useCallback, Fragment } from "react";
import { PlxsCallout, PlxsText } from "@plexlogic/plexus-ui";
import WizardRadioButtonGroup from "../WizardRadioButtonGroup";
import ConfirmationTable from "./ConfirmationTable";
import useUploadStore, {
  getHeaderRowColumns,
  getEntryRow1Columns,
  getEntryRow2Columns,
  useIsHeaderRow,
  useFileStructure,
  useUniqueIdColumn,
  useTicketCountColumn,
  useParticipantIdentificationColumn,
} from "./useUploadStore";
import { isNumeric } from "../../utils/validationUtils";

interface AdvancedEntriesUploadProps {
  onValidityChange?: (isValid: boolean) => void;
  isDisabled?: boolean;
}

const AdvancedEntriesUpload: React.FC<AdvancedEntriesUploadProps> = ({
  onValidityChange,
  isDisabled = false,
}) => {
  // Use proper typed selectors - following store boundaries
  const entriesUpload = useUploadStore((state) => state.entriesUpload);
  const setHeaderRow = useUploadStore((state) => state.setHeaderRow);
  const setFileStructure = useUploadStore((state) => state.setFileStructure);
  const setUniqueIdColumn = useUploadStore((state) => state.setUniqueIdColumn);
  const setTicketCountColumn = useUploadStore(
    (state) => state.setTicketCountColumn
  );
  const setParticipantIdentificationColumn = useUploadStore(
    (state) => state.setParticipantIdentificationColumn
  );

  // Get current values using typed selectors - proper store boundaries
  const isHeaderRowAnswer = useIsHeaderRow() ?? null;
  const fileStructureAnswer = useFileStructure() || null;
  const uniqueIdColumn = useUniqueIdColumn() || null;
  const ticketCountColumn = useTicketCountColumn() || null;
  const participantIdentificationColumn = useParticipantIdentificationColumn();

  // Only keep UI-specific state that's not persisted
  const [noNumericColumnsError, setNoNumericColumnsError] =
    useState<boolean>(false);

  // Get columns and validate numeric columns
  const getColumnsAndValidateNumeric = () => {
    const headerRowColumns = getHeaderRowColumns(entriesUpload);
    const entryRow1Columns = getEntryRow1Columns(entriesUpload);

    if (!headerRowColumns.length && !entryRow1Columns.length)
      return { columns: [], numericColumns: [] };

    // Handle entry row case differently - use Column 1, Column 2, etc.
    const columns =
      isHeaderRowAnswer === false
        ? entryRow1Columns.map((_: any, idx: number) => `Column ${idx + 1}`)
        : headerRowColumns.length > 0
        ? headerRowColumns
        : entryRow1Columns.map((_: any, idx: number) => `Column ${idx + 1}`);

    const numericColumns: string[] = [];

    // For entry row case (Question 1 = "entry row"), validate headerRowColumns for numeric values
    if (isHeaderRowAnswer === false) {
      // When it's entry row, headerRowColumns becomes the first data row
      if (headerRowColumns.length && entryRow1Columns.length) {
        columns.forEach((column: string, idx: number) => {
          const val1 = headerRowColumns[idx]; // First data row
          const val2 = entryRow1Columns[idx]; // Second data row

          if (isNumeric(val1) && isNumeric(val2)) {
            numericColumns.push(column);
          }
        });
      }
    } else {
      // For header row case, use the original logic
      const entryRow2Columns = getEntryRow2Columns(entriesUpload);
      if (entryRow1Columns.length && entryRow2Columns.length) {
        columns.forEach((column: string, idx: number) => {
          const val1 = entryRow1Columns[idx];
          const val2 = entryRow2Columns[idx];

          if (isNumeric(val1) && isNumeric(val2)) {
            numericColumns.push(column);
          }
        });
      }
    }

    return { columns, numericColumns };
  };

  const { numericColumns } = getColumnsAndValidateNumeric();
  // Validation function to check if all required questions are answered
  const validateAllQuestions = useCallback((): boolean => {
    return validateAdvancedEntries({
      isHeaderRow: isHeaderRowAnswer,
      fileStructure: fileStructureAnswer,
      uniqueIdColumn,
      ticketCountColumn,
      numericColumnsCount: numericColumns.length,
    });
  }, [
    isHeaderRowAnswer,
    fileStructureAnswer,
    uniqueIdColumn,
    ticketCountColumn,
    numericColumns.length,
  ]);

  // Validate form whenever answers change (only one useEffect for validation)
  useEffect(() => {
    const isValid = validateAllQuestions();
    onValidityChange?.(isValid);
  }, [
    isHeaderRowAnswer,
    fileStructureAnswer,
    uniqueIdColumn,
    ticketCountColumn,
    numericColumns.length,
    onValidityChange,
    validateAllQuestions,
  ]);

  // Question options
  const headerRowOptions = [
    { value: "true", label: "Yes" },
    { value: "false", label: "No" },
  ];

  const fileStructureOptions = [
    { value: "one-entry-per-row", label: "One entry per row" },
    {
      value: "count-per-entrant",
      label: "A column lists the entry count",
    },
  ];

  // Handle answer changes - directly update store, no local state
  const handleHeaderRowChange = (value: string) => {
    const answer = value === "true";
    setHeaderRow(answer);
    // Reset subsequent answers when Q1 changes
    setFileStructure(null as any);
    setUniqueIdColumn("");
    setTicketCountColumn("");
    setNoNumericColumnsError(false);
  };

  const handleFileStructureChange = (value: string) => {
    const answer = value as "one-entry-per-row" | "count-per-entrant";
    setFileStructure(answer);
    // Reset subsequent answers when Q2 changes
    setUniqueIdColumn("");
    setTicketCountColumn("");
    setParticipantIdentificationColumn(undefined); // Also reset participant identification
    setNoNumericColumnsError(false);

    // Check for numeric columns if count-per-entrant selected
    if (answer === "count-per-entrant" && numericColumns.length === 0) {
      setNoNumericColumnsError(true);
    }
  };

  // Wrapper functions to handle null values for ConfirmationTable callbacks
  const handleTicketCountColumnChange = (column: string | null) => {
    setTicketCountColumn(column || "");
  };

  const handleUniqueIdColumnChange = (column: string | null) => {
    setUniqueIdColumn(column || "");
    
    // For one-entry-per-row: automatically set participant identification to the same column
    // This avoids asking the user to select the same column twice
    if (fileStructureAnswer === "one-entry-per-row") {
      setParticipantIdentificationColumn(column || undefined);
    }
  };

  const handleParticipantIdentificationColumnChange = (
    column: string | null
  ) => {
    setParticipantIdentificationColumn(column || undefined);
  };

  // Determine what to show based on branching logic
  // Don't show subsequent questions if user selected "No" for header row
  const showQ2 = isHeaderRowAnswer === true;
  const showQ3OrTable = showQ2 && fileStructureAnswer !== null;
  
  // Track whether user is in entry row mode (for table display purposes)
  const isEntryRowMode = isHeaderRowAnswer === false;

  // For count-per-entrant: show table first (Q3)
  const showQ3ForCountPerEntrant =
    showQ3OrTable &&
    fileStructureAnswer === "count-per-entrant" &&
    numericColumns.length > 0;

  // For one-entry-per-row: show unique ID selection (Q3)
  const showQ3ForOneEntryPerRow =
    showQ3OrTable && fileStructureAnswer === "one-entry-per-row";

  // Q4: Show participant name column selection (optional) after Q3 is completed
  // For count-per-entrant with no numeric columns, show Q4 immediately after Q2 (regardless of header row)
  // Hide Q4 for one-entry-per-row (it's automatically set to the same as uniqueIdColumn)
  const showQ4ParticipantName =
    fileStructureAnswer !== "one-entry-per-row" && // Don't show for one-entry-per-row
    ((showQ3ForCountPerEntrant && ticketCountColumn !== null) ||
      (showQ3OrTable &&
        fileStructureAnswer === "count-per-entrant" &&
        numericColumns.length === 0));

  return (
    <Fragment>
      {/* Question 1: Is this a header row or entry row? */}
      <Fragment>
        <PlxsText size="sm" weight="bold">
          Is the first row a header?
        </PlxsText>
        <WizardRadioButtonGroup
          dataTestId="header-row-radio-group"
          name="header-row"
          options={headerRowOptions}
          value={isHeaderRowAnswer === null ? "" : isHeaderRowAnswer.toString()}
          onChange={handleHeaderRowChange}
          isDisabled={isDisabled}
        />

        {/* Show urgent warning if user selects "No" for header row */}
        {isHeaderRowAnswer === false && (
          <PlxsCallout
            dataTestId="no-header-row-warning-advanced"
            tone="urgent"
            message="Your file needs a header row – re-upload with column names in the first row."
          />
        )}
      </Fragment>

      {/* Question 2: How is your file structured? */}
      {showQ2 && (
        <Fragment>
          <PlxsText size="sm" weight="bold">
            How are entries counted?
          </PlxsText>
          <WizardRadioButtonGroup
            dataTestId="file-structure-radio-group"
            name="file-structure"
            options={fileStructureOptions}
            value={fileStructureAnswer || ""}
            onChange={handleFileStructureChange}
            isDisabled={isDisabled}
          />
        </Fragment>
      )}

      {/* Error for no numeric columns */}
      {noNumericColumnsError && (
        <PlxsCallout
          dataTestId="no-numeric-columns-callout"
          tone="urgent"
          message="Your file doesn’t contain a valid entry/ticket count column."
          supportingCopy="Please include a complete column with the number of entries or update your file structure selection."
        />
      )}

      {/* Question 3 for count-per-entrant: Select ticket count column */}
      {showQ3ForCountPerEntrant && (
        <div>
          <PlxsText size="sm" weight="bold">
            Which column holds the entry count?
          </PlxsText>
          <div
            style={{
              marginTop: "var(--plexus-space-sm, 8px)",
              marginBottom: "var(--plexus-space-md, 16px)",
            }}
          >
            <PlxsText size="sm">
              Choose from the numeric columns below. Only one column can be
              selected as the ticket count. Non-numeric columns are disabled and
              cannot be selected.
            </PlxsText>
          </div>

          {/* Enhanced table with ticket count selection mode */}
          <ConfirmationTable
            entriesUpload={entriesUpload}
            mode="ticket-count-selection"
            ticketCountColumn={ticketCountColumn || undefined}
            onTicketCountColumnChange={handleTicketCountColumnChange}
            numericOnlyColumns={numericColumns}
            isEntryRow={isEntryRowMode}
            isReadOnly={isDisabled}
          />
        </div>
      )}

      {/* Question 3 for one-entry-per-row: Select unique identifier column */}
      {showQ3ForOneEntryPerRow && (
        <Fragment>
          <PlxsText size="sm" weight="bold">
            Which column identifies each entrant?
          </PlxsText>
          <div
            style={{
              marginTop: "var(--plexus-space-sm, 8px)",
              marginBottom: "var(--plexus-space-md, 16px)",
            }}
          >
            <PlxsText size="sm">
              Use a column with unique values – a name, email or ID.
            </PlxsText>
          </div>

          {/* Enhanced table with unique ID selection mode */}
          <ConfirmationTable
            entriesUpload={entriesUpload}
            mode="unique-id-selection"
            uniqueIdColumn={uniqueIdColumn || undefined}
            onUniqueIdColumnChange={handleUniqueIdColumnChange}
            numericOnlyColumns={numericColumns}
            ticketCountColumn={ticketCountColumn || undefined}
            isEntryRow={isEntryRowMode}
            isReadOnly={isDisabled}
          />
        </Fragment>
      )}

      {/* Question 4 (Optional): Select winner identification column */}
      {showQ4ParticipantName && (
        <Fragment>
          <div
            style={{
              marginTop: "var(--plexus-space-md, 16px)",
              marginBottom: "var(--plexus-space-md, 16px)",
            }}
          >
            <PlxsText size="sm" weight="bold">
              Winner identification column
            </PlxsText>
            <PlxsText size="sm">
              Pick the column we'll use to identify winners – ideally one with
              unique values, like a name or ID. Leave it blank and we'll use the
              row number.
            </PlxsText>
          </div>

          {/* Table for participant identification selection - using unique-id-selection mode */}
          <ConfirmationTable
            entriesUpload={entriesUpload}
            mode="unique-id-selection"
            uniqueIdColumn={participantIdentificationColumn}
            onUniqueIdColumnChange={handleParticipantIdentificationColumnChange}
            numericOnlyColumns={numericColumns}
            isEntryRow={isEntryRowMode}
            isReadOnly={isDisabled}
          />
        </Fragment>
      )}
    </Fragment>
  );
};

// Export the validation function for external use
export const validateAdvancedEntries = (params: {
  isHeaderRow: boolean | null;
  fileStructure: "one-entry-per-row" | "count-per-entrant" | null;
  uniqueIdColumn: string | null;
  ticketCountColumn: string | null;
  numericColumnsCount: number;
}): boolean => {
  const {
    isHeaderRow,
    fileStructure,
    uniqueIdColumn,
    ticketCountColumn,
    numericColumnsCount,
  } = params;

  // Q1 must always be answered
  if (isHeaderRow === null) return false;

  // Q2 must always be answered after Q1
  if (fileStructure === null) return false;

  // For count-per-entrant flow
  if (fileStructure === "count-per-entrant") {
    // If no numeric columns, show error but allow to proceed (validation happens on server)
    if (numericColumnsCount === 0) return true;

    // Q3: Must select ticket count column if numeric columns exist
    if (ticketCountColumn === null) return false;

    return true;
  }

  // For one-entry-per-row flow
  if (fileStructure === "one-entry-per-row") {
    // Q3: Must select unique ID column (always required for deduplication)
    if (uniqueIdColumn === null) return false;

    return true;
  }

  return false;
};

export default AdvancedEntriesUpload;
